// Settings → Models: which models and effort levels the company allows, the
// defaults new chats start with, and the credit limit per run. Owners and
// Admins change it; members read it. Same load, revision and 409 pattern as
// AI processing (processing-settings.tsx).
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { CompanyRequestError } from './api';
import { useCompany } from './context';
import { Callout } from '@/components/ui/content';
import { useAppLocale } from '@/i18n/context';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import modelPickerCopy, { type ModelPickerCopyKey } from '@/i18n/ns/model-picker';
import {
  EFFORTS,
  fetchModelPolicy,
  modelName,
  saveModelPolicy,
  type Effort,
  type ModelPolicy,
  type ModelPolicyState,
} from '@/workspace/model-picker/model-policy';
import {
  EffortGlyph,
  costLabel,
  effortLabel,
  formatCredits,
  modelBody,
} from '@/workspace/model-picker/model-picker';
import { adoptModelPolicy } from '@/workspace/model-picker/store';

type Copy = Readonly<Record<ModelPickerCopyKey, string>>;

type Draft = Pick<
  ModelPolicy,
  'allowedChoices' | 'defaultChoice' | 'allowedEfforts' | 'defaultEffort'
> & { cap: string };

const draftOf = (policy: ModelPolicy): Draft => ({
  allowedChoices: policy.allowedChoices,
  defaultChoice: policy.defaultChoice,
  allowedEfforts: policy.allowedEfforts,
  defaultEffort: policy.defaultEffort,
  cap: String(policy.runCapCredits),
});

const sameSet = (a: string[], b: string[]) =>
  a.length === b.length && a.every((value) => b.includes(value));

const toggle = <T extends string>(values: T[], value: T, on: boolean, order: readonly T[]) =>
  order.filter((item) => (item === value ? on : values.includes(item)));

export function CompanyModelSettings({
  onEditStateChange,
}: {
  onEditStateChange: (state: { dirty: boolean; busy: boolean }) => void;
}) {
  const copy = useNamespaceCopy(modelPickerCopy);
  const locale = useAppLocale();
  const { data } = useCompany();
  const companyId = data?.company?.id;
  const company = data?.company?.name ?? '';
  const owner = ['owner', 'admin'].includes(data?.membership?.role ?? '');
  const [state, setState] = useState<ModelPolicyState | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [phase, setPhase] = useState<'loading' | 'ready' | 'error' | 'off'>('loading');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const generation = useRef(0);

  const adopt = (next: ModelPolicyState) => {
    setState(next);
    setDraft(draftOf(next.policy));
    adoptModelPolicy(next);
  };
  const load = async (): Promise<number | null> => {
    const current = ++generation.current;
    setPhase('loading');
    setBusy(false);
    setError(null);
    setNotice(null);
    setSaved(false);
    try {
      if (!companyId) return null;
      const result = await fetchModelPolicy(companyId);
      if (generation.current !== current) return null;
      if (!result) {
        setPhase('off');
        return null;
      }
      adopt(result);
      setPhase('ready');
      return current;
    } catch {
      if (generation.current === current) setPhase('error');
      return null;
    }
  };
  useEffect(() => void load(), [companyId]);
  useEffect(
    () => () => {
      generation.current += 1;
    },
    [],
  );

  const limits = state?.available.runCapCredits;
  const cap = Number(draft?.cap);
  const capValid = Boolean(
    limits &&
    draft?.cap.trim() &&
    Number.isSafeInteger(cap) &&
    cap >= limits.min &&
    cap <= limits.max,
  );
  const dirty = Boolean(
    state &&
    draft &&
    (!sameSet(draft.allowedChoices, state.policy.allowedChoices) ||
      !sameSet(draft.allowedEfforts, state.policy.allowedEfforts) ||
      draft.defaultChoice !== state.policy.defaultChoice ||
      draft.defaultEffort !== state.policy.defaultEffort ||
      cap !== state.policy.runCapCredits),
  );
  useEffect(() => onEditStateChange({ dirty, busy }), [dirty, busy, onEditStateChange]);

  const change = (next: Partial<Draft>) => {
    setDraft((current) => (current ? { ...current, ...next } : current));
    setSaved(false);
  };
  const save = async () => {
    if (!state || !draft || !capValid) return;
    const current = generation.current;
    const revision = state.policy.revision;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await saveModelPolicy({
        organizationId: state.policy.organizationId,
        revision,
        allowedChoices: draft.allowedChoices,
        defaultChoice: draft.defaultChoice,
        allowedEfforts: draft.allowedEfforts,
        defaultEffort: draft.defaultEffort,
        runCapCredits: cap,
      });
      if (generation.current !== current) return;
      adopt(result);
      setSaved(true);
    } catch (cause) {
      if (generation.current !== current) return;
      if (cause instanceof CompanyRequestError && cause.status === 409) {
        const reloaded = await load();
        if (reloaded !== null && generation.current === reloaded) setNotice(copy.conflict);
        return;
      }
      setError(copy.saveError);
    } finally {
      if (generation.current === current) setBusy(false);
    }
  };

  const header = (
    <>
      <h1>{copy.settingsTitle}</h1>
      <p className="company-description">{formatCopy(copy.settingsIntro, { company })}</p>
      {!owner && phase === 'ready' && (
        <p className="company-processing-member-note">{copy.adminOnly}</p>
      )}
    </>
  );
  if (phase !== 'ready' || !state || !draft)
    return (
      <>
        {header}
        {phase === 'error' ? (
          <div>
            <p role="alert" className="company-error">
              {copy.loadError}
            </p>
            <button type="button" className="company-secondary" onClick={() => void load()}>
              {copy.retry}
            </button>
          </div>
        ) : (
          <p role="status">{phase === 'off' ? copy.unavailable : copy.loading}</p>
        )}
      </>
    );

  const locked = !owner || busy;
  const models = state.available.choices;
  const efforts = EFFORTS.filter((effort) =>
    models.some((model) => model.efforts.includes(effort)),
  );
  const credits = (amount: number) => formatCredits(copy, amount, locale);
  return (
    <>
      {header}
      <form
        className="company-form company-models"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <Section title={copy.modelHeading} help={copy.modelsHelp}>
          {models.map((model) => {
            const on = draft.allowedChoices.includes(model.choice);
            const isDefault = draft.defaultChoice === model.choice;
            const cost = costLabel(copy, model.cost);
            return (
              <PolicyRow
                key={model.choice}
                name={modelName(model.choice, model.name)}
                body={modelBody(copy, model.choice)}
                badges={
                  <>
                    {isDefault && (
                      <span className="company-models-default">{copy.defaultBadge}</span>
                    )}
                    {cost && <span className="pc-model-cost">{cost}</span>}
                  </>
                }
                on={on}
                isDefault={isDefault}
                locked={locked}
                copy={copy}
                onToggle={(next) =>
                  change({
                    allowedChoices: toggle(
                      draft.allowedChoices,
                      model.choice,
                      next,
                      models.map((item) => item.choice),
                    ),
                  })
                }
                onMakeDefault={() => change({ defaultChoice: model.choice })}
              />
            );
          })}
        </Section>

        <Section title={copy.effortHeading} help={copy.effortsHelp}>
          {efforts.map((effort) => {
            const on = draft.allowedEfforts.includes(effort);
            const isDefault = draft.defaultEffort === effort;
            return (
              <PolicyRow
                key={effort}
                icon={<EffortGlyph effort={effort} size={16} />}
                name={effortLabel(copy, effort)}
                body={copy[`${effort}Body` as const]}
                badges={
                  isDefault && <span className="company-models-default">{copy.defaultBadge}</span>
                }
                on={on}
                isDefault={isDefault}
                locked={locked}
                copy={copy}
                onToggle={(next) =>
                  change({
                    allowedEfforts: toggle(draft.allowedEfforts, effort, next, EFFORTS) as Effort[],
                  })
                }
                onMakeDefault={() => change({ defaultEffort: effort })}
              />
            );
          })}
        </Section>

        {owner ? (
          <CapField
            copy={copy}
            value={draft.cap}
            invalid={!capValid}
            locked={busy}
            range={formatCopy(copy.capRange, {
              min: credits(state.available.runCapCredits.min),
              max: credits(state.available.runCapCredits.max),
            })}
            onChange={(value) => change({ cap: value })}
          />
        ) : (
          <section className="company-models-section">
            <h2 className="company-section-title">{copy.capHeading}</h2>
            <p className="company-models-help">
              {formatCopy(copy.capNow, { cap: credits(state.policy.runCapCredits) })}
            </p>
          </section>
        )}

        {(dirty || busy || saved || error || notice) && (
          <div className="company-processing-decide">
            {dirty && (
              <p className="company-processing-decision" role="status">
                {formatCopy(copy.decision, { company })}
              </p>
            )}
            {notice && (
              <div role="status">
                <Callout tone="info">
                  <p>{notice}</p>
                </Callout>
              </div>
            )}
            {error && (
              <p role="alert" className="company-error">
                {error}
              </p>
            )}
            {owner && (dirty || busy || saved) && (
              <div className="company-form-actions">
                <button className="company-primary" disabled={busy || !dirty || !capValid}>
                  {busy ? copy.saving : copy.save}
                </button>
                {saved && !dirty && (
                  <span role="status" className="company-processing-saved">
                    {copy.saved}
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </form>
    </>
  );
}

function Section({ title, help, children }: { title: string; help: string; children: ReactNode }) {
  const id = useId();
  return (
    <section className="company-models-section" aria-labelledby={id}>
      <h2 id={id} className="company-section-title">
        {title}
      </h2>
      <p className="company-models-help">{help}</p>
      <div className="company-models-card">{children}</div>
    </section>
  );
}

function PolicyRow({
  icon,
  name,
  body,
  badges,
  on,
  isDefault,
  locked,
  copy,
  onToggle,
  onMakeDefault,
}: {
  icon?: ReactNode;
  name: string;
  body: string;
  badges?: ReactNode;
  on: boolean;
  isDefault: boolean;
  locked: boolean;
  copy: Copy;
  onToggle: (on: boolean) => void;
  onMakeDefault: () => void;
}) {
  const nameId = useId();
  const bodyId = useId();
  // The default stays on: another row becomes the default first.
  const switchLocked = locked || isDefault;
  return (
    <div className="company-models-row" data-on={on || undefined}>
      {icon && <span className="company-models-icon">{icon}</span>}
      <span className="company-models-copy">
        <span className="company-models-name">
          <span id={nameId}>{name}</span>
          {badges}
        </span>
        <span id={bodyId} className="company-models-body">
          {body}
          {isDefault && !locked && <span className="ws-sr-only"> {copy.defaultLocked}</span>}
        </span>
      </span>
      {!locked && on && !isDefault && (
        <button type="button" className="company-models-make-default" onClick={onMakeDefault}>
          {copy.makeDefault}
        </button>
      )}
      <button
        type="button"
        role="switch"
        className="company-models-switch"
        aria-checked={on}
        aria-labelledby={nameId}
        aria-describedby={bodyId}
        aria-disabled={switchLocked || undefined}
        title={isDefault && !locked ? copy.defaultLocked : undefined}
        onClick={() => !switchLocked && onToggle(!on)}
      >
        <span aria-hidden="true" />
      </button>
    </div>
  );
}

function CapField({
  copy,
  value,
  invalid,
  locked,
  range,
  onChange,
}: {
  copy: Copy;
  value: string;
  invalid: boolean;
  locked: boolean;
  range: string;
  onChange: (value: string) => void;
}) {
  const id = useId();
  const help = useId();
  return (
    <section className="company-models-section" aria-labelledby={`${id}-title`}>
      <h2 id={`${id}-title`} className="company-section-title">
        {copy.capHeading}
      </h2>
      <p className="company-models-help">{copy.capHelp}</p>
      <div className="company-models-cap">
        <label htmlFor={id}>{copy.capField}</label>
        <span className="company-models-money" data-invalid={invalid || undefined}>
          <input
            id={id}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            value={value}
            readOnly={locked}
            aria-invalid={invalid || undefined}
            aria-describedby={help}
            onChange={(event) => onChange(event.target.value)}
          />
          <span className="company-models-unit">{copy.capUnit}</span>
        </span>
        <span id={help} className="company-models-range" data-invalid={invalid || undefined}>
          {range}
        </span>
      </div>
    </section>
  );
}
